import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isLocalAuthBypassEnabled } from 'config/localAuthBypass';
import { setAccessToken } from 'services/api';

// Enterprise entry points wait for their own identity's token without loading
// other products or changing the application's Auth0 session lifecycle.
export function useEnterpriseSession(revision = 0) {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently } =
    useAuth0();
  const local = isLocalAuthBypassEnabled();
  const identity = user?.sub || (local ? 'local' : null);
  const [state, setState] = useState({ ready: false, error: null });

  useEffect(() => {
    let active = true;
    setState({ identity, revision, ready: false, error: null });
    if (!local && isLoading) return undefined;
    (async () => {
      if (!local) {
        if (!isAuthenticated || !identity)
          throw new Error('Session unavailable');
        const token = await getAccessTokenSilently();
        if (!active) return;
        if (!token) throw new Error('Session unavailable');
        setAccessToken(token);
      }
      if (active) setState({ identity, revision, ready: true, error: null });
    })().catch((error) => {
      if (active) setState({ identity, revision, ready: false, error });
    });
    return () => {
      active = false;
    };
  }, [
    local,
    isLoading,
    isAuthenticated,
    identity,
    getAccessTokenSilently,
    revision,
  ]);

  const current = state.identity === identity && state.revision === revision;
  return {
    identity,
    ready: current && state.ready && (local || (!isLoading && isAuthenticated)),
    error: current ? state.error : null,
  };
}
